import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled, { keyframes } from 'styled-components';

import { useFakturowniaSync } from 'core/hooks';

interface IFakturowniaSyncChipProps {
  /** Called once the sync poll resolves so the page can refresh its lists. */
  onSynced?: () => void;
}

const fmtMoment = (value?: string | null): string =>
  value ? moment(value).format('DD.MM HH:mm') : '';

/**
 * Live status pill + manual «Sync now» trigger (TZ §3.1). One self-contained
 * component — the wrapping page just mounts it and reacts to the `onSynced`
 * callback so it can refetch its data on completion.
 */
export const FakturowniaSyncChip = memo(
  ({ onSynced }: IFakturowniaSyncChipProps) => {
    const { t } = useTranslation();
    const { status, syncing, triggerSync } = useFakturowniaSync(onSynced);

    const lastSuccess = status?.fakturowniaSyncStatus?.lastSuccess;
    const lastRun = status?.fakturowniaSyncStatus?.lastRun;
    const failed = lastRun?.status === 'FAILED';

    const variant: ChipVariant = syncing
      ? 'warn'
      : failed
      ? 'error'
      : lastSuccess
      ? 'ok'
      : 'neutral';

    const label = syncing
      ? t('pages.fakturownia.sync.running')
      : failed
      ? t('pages.fakturownia.sync.errorTitle')
      : lastSuccess
      ? t('pages.fakturownia.sync.lastSyncAt', {
          date: fmtMoment(lastSuccess.startedAt),
        })
      : '—';

    return (
      <Wrap>
        <Chip variant={variant} title={lastRun?.errorMessage || undefined}>
          {syncing && <Spinner />}
          {label}
        </Chip>
        <SyncButton type="button" disabled={syncing} onClick={triggerSync}>
          {syncing ? <Spinner /> : null}
          {t('pages.fakturownia.sync.syncNow')}
        </SyncButton>
      </Wrap>
    );
  },
);

type ChipVariant = 'warn' | 'ok' | 'error' | 'neutral';

const CHIP_COLORS: Record<
  ChipVariant,
  { bg: string; border: string; fg: string }
> = {
  warn: { bg: '#fffbeb', border: '#fde68a', fg: '#92400e' },
  ok: { bg: '#f0fdf4', border: '#bbf7d0', fg: '#166534' },
  error: { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' },
  neutral: { bg: '#f8fafc', border: '#e2e8f0', fg: '#475569' },
};

const Wrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Chip = styled.span<{ variant: ChipVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ variant }) => CHIP_COLORS[variant].bg};
  border: 1px solid ${({ variant }) => CHIP_COLORS[variant].border};
  color: ${({ variant }) => CHIP_COLORS[variant].fg};
`;

const SyncButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #2563eb;
  background: #2563eb;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1d4ed8;
    border-color: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;
